import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Search, LayoutDashboard, Calendar, MessageSquare, Menu,
  Briefcase, User, Shield,
} from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { useMobileNav } from '../../contexts/MobileNavContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Native app bottom tab bar. The tabs mirror the web navbar's primary links for
 * the user's CURRENT role and update automatically when the role switcher changes
 * the active role. The last tab opens the menu drawer (footer info + settings).
 */
export const MobileBottomTabBar = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { setMenuOpen } = useMobileNav();
  const [badges, setBadges] = useState({});

  const effectiveRole = user?.active_role || user?.role;

  // Reserve space so fixed content isn't hidden behind the bar.
  useEffect(() => {
    document.body.classList.add('has-mobile-tabbar');
    return () => document.body.classList.remove('has-mobile-tabbar');
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const fetchBadges = async () => {
      try {
        const r = await axios.get(`${API}/badge-counts`);
        if (active) setBadges(r.data || {});
      } catch (e) {
        /* noop */
      }
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    const handler = () => fetchBadges();
    window.addEventListener('refreshBadgeCounts', handler);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('refreshBadgeCounts', handler);
    };
  }, [user, effectiveRole]);

  if (!user) return null;

  const num = (v) => Number(v || 0);
  let tabs;
  if (effectiveRole === 'admin') {
    tabs = [
      { label: 'Admin', icon: Shield, path: '/admin', dot: num(badges.admin_actionable) > 0 },
      ...(user.is_owner ? [{ label: 'Owner', icon: Briefcase, path: '/owner' }] : []),
      { label: 'Profile', icon: User, path: '/profile' },
    ];
  } else if (effectiveRole === 'provider') {
    tabs = [
      { label: 'Jobs', icon: Search, path: '/jobs' },
      { label: 'Home', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Bookings', icon: Calendar, path: '/my-bookings', dot: num(badges.pending_bookings) + num(badges.new_bids) + num(badges.active_disputes) > 0 },
      { label: 'Messages', icon: MessageSquare, path: '/inquiries', dot: num(badges.unread_messages) > 0 },
    ];
  } else {
    tabs = [
      { label: 'Services', icon: Search, path: '/services' },
      { label: 'Home', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Bookings', icon: Calendar, path: '/my-bookings', dot: num(badges.pending_bookings) + num(badges.active_disputes) > 0 },
      { label: 'Messages', icon: MessageSquare, path: '/inquiries', dot: num(badges.unread_messages) > 0 },
    ];
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="mobile-bottom-tabbar"
    >
      <div className="flex items-stretch justify-around h-16">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = isActive(t.path);
          return (
            <button
              key={t.path}
              onClick={() => navigate(t.path)}
              className="relative flex-1 flex flex-col items-center justify-center gap-0.5 active:scale-95 transition"
              data-testid={`tab-${t.label.toLowerCase()}`}
            >
              <Icon className={`w-[22px] h-[22px] ${active ? 'text-rose-600' : 'text-gray-400'}`} strokeWidth={active ? 2.4 : 2} />
              <span className={`text-[10px] font-medium ${active ? 'text-rose-600' : 'text-gray-500'}`}>{t.label}</span>
              {t.dot && <span className="absolute top-2.5 right-[26%] w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />}
            </button>
          );
        })}
        <button
          onClick={() => setMenuOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 active:scale-95 transition"
          data-testid="tab-menu"
        >
          <Menu className="w-[22px] h-[22px] text-gray-400" />
          <span className="text-[10px] font-medium text-gray-500">Menu</span>
        </button>
      </div>
    </nav>
  );
};

export default MobileBottomTabBar;
